// FinalsGallery2026 — private photo gallery for the 2026 IRO Finalists (/2026-finals-gallery)
// Unlisted: routed but intentionally NOT linked from the navbar. Share the URL directly.
import { useEffect } from 'react';
import * as React from 'react';
import Seo from '../components/Seo';

const BLUE = '#004d99';
const INK = '#1a1a2e';
const BODY = '#42475a';

const DIR = '/2026-finals-gallery';

// Group photos from the in-person finals (Harvard, June 2026).
const GROUPS = [
  'group-inside-1', 'group-inside-2', 'group-inside-3', 'group-inside-4', 'group-inside-5', 'group-inside-6',
  'group-outside-1', 'group-outside-2', 'group-outside-3', 'group-outside-4', 'group-outside-5', 'group-outside-6',
].map((f, i) => ({ file: f, caption: `Finals group photo ${i + 1}` }));

// Individual finalist photos. Multiple shots per finalist keep their numbered captions.
const PORTRAITS = [
  { file: 'andrey-vasilyev-1', caption: 'Andrey Vasilyev' },
  { file: 'andrey-vasilyev-2', caption: 'Andrey Vasilyev' },
  { file: 'vriti-mirchandani-1', caption: 'Vriti Mirchandani' },
  { file: 'vriti-mirchandani-2', caption: 'Vriti Mirchandani' },
  { file: 'kyle-yang', caption: 'Kyle Yang' },
  { file: 'zaki-farhat', caption: 'Zaki Farhat' },
  { file: 'zaki-farhat-1', caption: 'Zaki Farhat' },
  { file: 'aditya-singhal-1', caption: 'Aditya Singhal' },
  { file: 'aditya-singhal-2', caption: 'Aditya Singhal' },
  { file: 'aditya-singhal-3', caption: 'Aditya Singhal' },
  { file: 'malavika-sreehari', caption: 'Malavika Sreehari' },
  { file: 'dhanvin-patel', caption: 'Dhanvin Patel' },
  { file: 'shyntas-aityk-1', caption: 'Shyntas Aityk' },
  { file: 'shyntas-aityk-2', caption: 'Shyntas Aityk' },
];

function Card({ file, caption }) {
  const src = `${DIR}/${file}.jpg`;
  return (
    <figure className="fg-card">
      <div className="fg-imgwrap">
        <img src={src} alt={caption} loading="lazy" />
      </div>
      <figcaption>
        <span className="fg-cap">{caption}</span>
        <a className="fg-dl" href={src} download={`${file}.jpg`}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 21h16" />
          </svg>
          Download
        </a>
      </figcaption>
    </figure>
  );
}

export default function FinalsGallery2026() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div style={{ background: '#f6f8fb' }}>
      <Seo title="2026 Finals Photos" path="/2026-finals-gallery" />
      <style>{`
        .fg-wrap { max-width: 1100px; margin: 0 auto; padding: 0 22px 90px; }
        .fg-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
        @media (max-width: 860px) { .fg-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 520px) { .fg-grid { grid-template-columns: 1fr; } }
        .fg-card {
          margin: 0; background: #fff; border: 1px solid #e7edf5; border-radius: 14px;
          overflow: hidden; box-shadow: 0 2px 10px rgba(16,42,77,0.05);
          transition: box-shadow .18s ease, transform .18s ease;
        }
        .fg-card:hover { box-shadow: 0 10px 28px rgba(0,77,153,0.14); transform: translateY(-2px); }
        .fg-imgwrap { width: 100%; aspect-ratio: 4 / 3; overflow: hidden; background: #eef2f8; }
        .fg-imgwrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .fg-card figcaption {
          display: flex; align-items: center; justify-content: space-between; gap: 10px;
          padding: 12px 14px;
        }
        .fg-cap { color: ${INK}; font-size: 14.5px; font-weight: 600; }
        .fg-dl {
          display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
          background: ${BLUE}; color: #fff; text-decoration: none;
          font-size: 13px; font-weight: 600; padding: 7px 13px; border-radius: 8px;
          transition: background .15s ease;
        }
        .fg-dl:hover { background: #013366; }
        .fg-section-h {
          color: ${INK}; font-size: 24px; font-weight: 700; margin: 52px 0 6px;
        }
        .fg-section-s { color: #6b7280; font-size: 14.5px; margin: 0 0 22px; }
      `}</style>

      {/* Hero */}
      <div style={{
        background: `linear-gradient(135deg, ${BLUE} 0%, #013366 100%)`,
        color: '#fff', padding: '64px 22px 56px', textAlign: 'center',
      }}>
        <div style={{
          display: 'inline-block', border: '1px solid rgba(255,255,255,0.4)',
          borderRadius: 999, padding: '5px 16px', fontSize: 12, letterSpacing: 2,
          textTransform: 'uppercase', marginBottom: 22, fontWeight: 600,
          color: 'rgba(255,255,255,0.92)',
        }}>
          Private · For 2026 Finalists
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 700, margin: '0 0 14px', lineHeight: 1.15 }}>
          2026 Finals Photos
        </h1>
        <p style={{ maxWidth: 600, margin: '0 auto', fontSize: 16, lineHeight: 1.6, color: 'rgba(255,255,255,0.85)' }}>
          Photos from the in-person Final Round at Harvard, June 2026. Thank you for being
          part of it. Download any photo below to keep your own copy.
        </p>
      </div>

      <div className="fg-wrap">
        <h2 className="fg-section-h" style={{ marginTop: 46 }}>Group photos</h2>
        <p className="fg-section-s">The whole cohort, inside and outside the venue.</p>
        <div className="fg-grid">
          {GROUPS.map((p) => <Card key={p.file} {...p} />)}
        </div>

        <h2 className="fg-section-h">Finalists</h2>
        <p className="fg-section-s">Individual photos from the finals.</p>
        <div className="fg-grid">
          {PORTRAITS.map((p) => <Card key={p.file} {...p} />)}
        </div>
      </div>
    </div>
  );
}
